/* eslint-disable */

import { getHigherUnitName, getUnitInLowerUnit } from '@/Helpers';

export const validationRules = [
	{
		name: 'hasRecipes',
		check: (recipes) => {
			if (!recipes?.length) {
				return 'Add at least one product recipe before saving.';
			}
			return null;
		},
	},
	{
		name: 'noAmbiguousRecipes',
		check: (recipes) => {
			const blue = (recipes || []).filter(
				(r) => r?.matchStatus === 'ambiguous'
			);
			if (blue.length > 0) {
				const names = blue
					.slice(0, 5)
					.map((r) => `"${r?.name || 'recipe'}"`)
					.join(', ');
				const remaining = blue.length - 5;
				return `Multiple products matched these recipes — please pick the right one for the blue rows: ${names}${remaining > 0 ? ` and ${remaining} more` : ''}.`;
			}
			return null;
		},
	},
	{
		name: 'noUnmatchedRecipes',
		check: (recipes) => {
			const redRecipes = (recipes || []).filter(
				(r) => r?.matchStatus !== 'ambiguous'
					&& (
						r?.matchStatus === 'unmatched'
						|| r?.productId == null
						|| r?.variantId == null
					)
			);
			if (redRecipes.length > 0) {
				const names = redRecipes
					.slice(0, 5)
					.map((r) => `"${r?.name || 'recipe'}"`)
					.join(', ');
				const remaining = redRecipes.length - 5;
				return `Please resolve the red (unmatched) recipes before saving: ${names}${remaining > 0 ? ` and ${remaining} more` : ''}. Click each red product name to pick the correct product, or use the + icon to create a new product.`;
			}
			return null;
		},
	},
	{
		name: 'recipeNameAndRows',
		check: (recipes) => {
			const invalid = recipes.find(
				(r) => !r?.name?.trim() || !r?.rows || r.rows.length === 0
			);
			if (invalid) {
				return `"${invalid?.name || 'A product recipe'}" is missing a name or has no raw material rows. Please add at least one row.`;
			}
			return null;
		},
	},
	{
		name: 'recipeLinkedToProduct',
		check: (recipes) => {
			const missing = recipes.find(
				(r) => r?.productId == null || r?.variantId == null
			);
			if (missing) {
				return 'Each product recipe must be linked to a product variant. Re-pick from "Add New Product Recipe".';
			}
			return null;
		},
	},
	{
		check: (recipes) => {
			for (const r of recipes) {
				const t = Number(r?.totalProcessTime);
				const b = Number(r?.batchSize);
				const fields = [];
				if (!Number.isFinite(t) || t <= 0) fields.push('Total Process Time');
				if (!Number.isFinite(b) || b <= 0) fields.push('Batch Size (kg)');
				if (fields.length > 0) {
					return `"${r?.name || 'A product recipe'}" is missing required field${fields.length > 1 ? 's' : ''}: ${fields.join(' and ')}. Please enter a value greater than 0.`;
				}
			}
			return null;
		},
	},
	{
		name: 'rowBaseQty',
		check: (recipes) => {
			const offending = recipes.find((r) =>
				(r?.rows || []).some(
					(row) => row?.use && (!row?.baseQty || Number(row.baseQty) <= 0)
				)
			);
			if (offending) {
				return `"${offending?.name || 'A product recipe'}" has rows with missing or zero quantity. Please enter a valid Base Qty for every used row.`;
			}
			return null;
		},
	},
	{
		name: 'noAmbiguousRows',
		check: (recipes) => {
			const blue = [];
			recipes.forEach((r) => {
				(r?.rows || []).forEach((row) => {
					if (row?.matchStatus === 'ambiguous') {
						blue.push(`${r?.name || 'recipe'} → ${row?.name || 'row'}`);
					}
				});
			});
			if (blue.length > 0) {
				const sample = blue.slice(0, 5).join('; ');
				const remaining = blue.length - 5;
				return `Multiple products matched these ingredient rows — please pick the right one for each blue row: ${sample}${remaining > 0 ? ` and ${remaining} more` : ''}.`;
			}
			return null;
		},
	},
	{
		name: 'noUnmatchedRows',
		check: (recipes) => {
			const unmatched = [];
			recipes.forEach((r) => {
				(r?.rows || []).forEach((row) => {
					if (row?.matchStatus === 'ambiguous') return;
					if (
						row?.matchStatus === 'unmatched'
						|| row?.productId == null
						|| row?.variantId == null
					) {
						unmatched.push(`${r?.name || 'recipe'} → ${row?.name || 'row'}`);
					}
				});
			});
			if (unmatched.length > 0) {
				const sample = unmatched.slice(0, 5).join('; ');
				const remaining = unmatched.length - 5;
				return `Please resolve these items — they are not found in our system: ${sample}${remaining > 0 ? ` and ${remaining} more` : ''}. Use the + icon next to a red row to create a new product.`;
			}
			return null;
		},
	},
	// {
	// 	name: 'notOverTarget',
	// 	check: (recipes) => {
	// 		const over = recipes.filter((r) => {
	// 			const target = Number(r?.batchSize) || 0;
	// 			if (target === 0) return true;
	// 			const calc = (r?.rows || [])
	// 				.filter((row) => row?.use)
	// 				.reduce((sum, row) => sum + (parseFloat(row?.baseQty) || 0), 0);
	// 			return calc - target > 0.1;
	// 		});
	// 		if (over.length > 0) {
	// 			const names = over.map((r) => `"${r?.name || 'recipe'}"`).join(', ');
	// 			return `Quantity is over target for: ${names}. Please reduce the row quantities so the calculated batch is within the target batch size.`;
	// 		}
	// 		return null;
	// 	},
	// },
];

// Runs all rules in order. Returns the first failure as
// `{ isValid: false, error: string }`, or `{ isValid: true }` if all pass.
export const validator = (recipes) => {
	for (const rule of validationRules) {
		const error = rule.check(recipes);
		if (error) {
			return { isValid: false, error, failedRule: rule.name };
		}
	}
	return { isValid: true };
};

export const deserializer = (recipe) => {
	const parentProductId = recipe?.productId;
	const parentVariantId = recipe?.variantId;

	const skus = (recipe?.rows || [])
		.filter((row) => row?.use && row?.productId != null && row?.variantId != null)
		.map((row) => {
			const baseQtyNum = Number(row?.baseQty) || 0;
			const containerWeightNum = row?.containerWeight
				? Number(row.containerWeight)
				: 0;
			const higherUnit = getHigherUnitName(row.unit) || row.unit;
			return {
				id: null,
				skuId: row.productId,
				variantId: row.variantId,
				unit: row.originalUnit,
				replaceable: false,
				containerWeight: row?.containerWeight
					? getUnitInLowerUnit(higherUnit, containerWeightNum)
					: null,
				pricing: {
					pricing: [
						{
							id: parentVariantId,
							qty: getUnitInLowerUnit(higherUnit, baseQtyNum),
							skip: !row?.use,
							minWeight: getUnitInLowerUnit(higherUnit, baseQtyNum),
							price: 0,
						},
					],
				},
			};
		});

	const processTime = Number(recipe?.totalProcessTime) || 0;

	const data = {
		id: null,
		productId: parentProductId,
		scheduledAtDelta: 0,
		recipeDescription: '',
		recipeURL: '',
		variantBatchSize: parentVariantId != null
			? { [parentVariantId]: getUnitInLowerUnit(recipe?.unit, Number(recipe?.batchSize) || 0) }
			: {},
		isBulk: false,
		weightTolerance: null,
		isIntermediate: false,
		stages: [
			{
				id: null,
				stageName: 'Stage 1',
				stageDescription: '',
				stationId: null,
				stageDependency: [],
				processes: [
					{
						id: null,
						processName: recipe?.name || 'Step 1',
						processDescription: '',
						processTime,
						customizationType: 'NONE',
						deltaPricing: false,
						customizationName: 'Unnamed Customization',
						removableProcess: false,
						addOnLimit: null,
						applicableSizeVariants: parentVariantId != null
							? String(parentVariantId)
							: '',
						skus,
					},
				],
			},
		],
		totalCookingTime: processTime,
	};

	return {
		data,
		params: {
			productId: parentProductId,
		},
	};
};

const config = {
	validationRules,
	validator,
	deserializer,
};

export default config;
